import { useCallback, useState } from "react";
import { flushSync } from "react-dom";
import { Controller, useForm } from "react-hook-form";

import { Edit, Trash } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Input from "ds/components/Input";
import { stringIsRequired } from "utils/formValidators";
import { HookType } from "utils/hooks";
import Typography from "ds/components/Typography";

import styles from "./styles.module.css";

type BeforeAfterCommandsListItemProps = {
  text: string;
  readOnly?: boolean;
  onRemoveCommand: (type: HookType, id: string) => void;
  type?: HookType;
  id: string;
  onEditCommand: (type: HookType, id: string, text: string) => void;
};

type BeforeAfterCommandsListItemFormData = {
  text: string;
};

const BeforeAfterCommandsListItem = ({
  text,
  readOnly,
  onRemoveCommand,
  type,
  id,
  onEditCommand,
}: BeforeAfterCommandsListItemProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<BeforeAfterCommandsListItemFormData>({
    defaultValues: {
      text,
    },
  });

  const { setFocus, handleSubmit, control } = form;

  const handleOpenEdit = useCallback(() => {
    flushSync(() => {
      setIsEditing(true);
    });

    setFocus("text");
  }, [setFocus]);

  const handleEdit = handleSubmit((formData: BeforeAfterCommandsListItemFormData) => {
    if (type) {
      onEditCommand(type, id, formData.text);
      setIsEditing(false);
    }
  });

  const handleDelete = useCallback(() => {
    if (type) {
      onRemoveCommand(type, id);
    }
  }, [type, id, onRemoveCommand]);

  if (isEditing && !readOnly) {
    return (
      <form onSubmit={handleEdit} className={styles.form}>
        <Controller
          name="text"
          control={control}
          rules={{
            onBlur: handleEdit,
            validate: stringIsRequired(),
          }}
          render={({ field, fieldState }) => (
            <Input {...field} placeholder="Enter a command" error={!!fieldState.error} />
          )}
        />
      </form>
    );
  }

  return (
    <Box justify="between" align="center" gap="medium" fullWidth>
      <Typography wordBreak tag="p" variant="p-t7">
        {text}
      </Typography>
      {!readOnly && (
        <Box gap="medium" shrink="0">
          <ButtonIcon icon={Edit} onPress={handleOpenEdit} variant="ghost">
            Edit
          </ButtonIcon>
          <ButtonIcon icon={Trash} onPress={handleDelete} variant="ghostDanger">
            Delete
          </ButtonIcon>
        </Box>
      )}
    </Box>
  );
};

export default BeforeAfterCommandsListItem;
